import React, { useState, useEffect, useRef } from "react";
import { IInputFilesModalProps } from "./definitions";
import { FileMetadata } from "../input-files/definitions";
import styles from "./style.module.scss";
import InputFiles from "components/input-files/InputFiles";
import Modal from "@amzn/awsui-components-react/polaris/modal";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Button from "@amzn/awsui-components-react/polaris/button";
import Box from "@amzn/awsui-components-react/polaris/box";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import Checkbox from "@amzn/awsui-components-react/polaris/checkbox";
import Flashbar from "@amzn/awsui-components-react/polaris/flashbar";
import ProgressBar from "@amzn/awsui-components-react/polaris/progress-bar";
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import Icon from "@amzn/awsui-components-react/polaris/icon";
import { useAppSelector } from "app/store/hooks";
import { validateFileMetadata } from "utils/input-files-utils";
import { useLocalNotifications } from "hooks";
import {
  getFileExtension,
  getFileNameWithoutExtension,
} from "utils/date-format";
import {
  checkBatchVersionCollisions,
  renderVersionCollisionMessage,
  createDocumentCollection,
} from "../documents-table/version-utils";
import {
  checkBatchVersionCollisionsOS,
  renderVersionCollisionMessageOS,
  VersionCollisionInfoOS,
} from "../documents-table-os/version-utils-os";
import {
  convertItemsToTempDocuments,
  convertStagedFilesToDocuments,
} from "./utils";
import {
  createInitialUploadProgress,
  UploadProgress,
  CancelToken,
} from "utils/upload-progress-utils";
import { DocumentNew } from "models/schemas";

const InputFilesModal: React.FC<IInputFilesModalProps> = (
  props: IInputFilesModalProps
) => {
  const teamName: string = useAppSelector((state) => state.audits.teamName);
  const [files, setFiles] = useState<File[]>([]);
  const [fileMetadata, setFileMetadata] = useState<FileMetadata[]>([]);
  const [isDisabled, setIsDisabled] = useState(false);
  const [fileError, setFileError] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [duplicateFiles, setDuplicateFiles] = useState<Set<string>>(new Set());
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>(
    createInitialUploadProgress()
  );
  const cancelTokenRef = useRef<CancelToken>({ cancelled: false });
  const frozenNotificationsRef = useRef<any[]>([]);
  const progressBarRef = useRef<HTMLDivElement>(null);

  // OpenSearch collision detection state
  const [showOSCollisionWarning, setShowOSCollisionWarning] = useState(false);
  const [osCollisions, setOSCollisions] = useState<VersionCollisionInfoOS[]>(
    []
  );
  const [osCollisionAcknowledged, setOSCollisionAcknowledged] = useState(false);
  const [isCheckingCollisions, setIsCheckingCollisions] = useState(false);

  const {
    notifications,
    addNotification: addLocalNotification,
    clearNotifications,
  } = useLocalNotifications();

  // Auto-scroll to progress bar when upload starts
  useEffect(() => {
    if (uploadProgress.isUploading && progressBarRef.current) {
      progressBarRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [uploadProgress.isUploading]);

  // Auto-scroll to progress bar when upload starts
  useEffect(() => {
    if (uploadProgress.isUploading && progressBarRef.current) {
      progressBarRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [uploadProgress.isUploading]);

  // Determine if we should use OpenSearch collision detection
  const useOpenSearchCollision =
    props.defaultDocumentType === "Team" && props.searchDocuments !== undefined;

  // Version collision detection (DynamoDB path only - passive notification)
  useEffect(() => {
    // Skip version collision detection during upload to prevent message updates
    if (uploadProgress.isUploading) {
      return;
    }

    clearNotifications();

    // Skip if using OpenSearch collision detection (active confirmation)
    if (useOpenSearchCollision) {
      return;
    }

    if (
      !props.visible ||
      !props.versioningEnabled ||
      !props.documentCollection ||
      !props.parentId ||
      props.enableReplaceMode
    ) {
      return;
    }

    // Use fileMetadata when enableRenaming/enableDocumentTypes is true
    // Otherwise use files array for team files
    const itemsToCheck = fileMetadata.length > 0 ? fileMetadata : files;

    if (itemsToCheck.length === 0) {
      return;
    }

    // Determine which collection to use for collision detection
    let collectionToUse = props.documentCollection;

    // Only create merged collection if there are staged files with pending versions
    if (props.stagedFiles && props.stagedFiles.length > 0) {
      const docMap = new Map<string, any>();

      // Add documents from existing collection
      const versionGroups = Array.from(
        props.documentCollection.versionGroupsMap.values()
      );
      versionGroups.forEach((group) => {
        group.allVersions.forEach((doc) => {
          docMap.set(doc.documentId, doc);
        });
      });

      const newStagedDocs = convertStagedFilesToDocuments(
        props.stagedFiles,
        props.parentId,
        teamName
      );
      newStagedDocs.forEach((doc) => {
        docMap.set(doc.documentId, doc);
      });

      const mergedDocuments = Array.from(docMap.values());
      collectionToUse = createDocumentCollection(mergedDocuments);
    }

    // Convert items to temporary documents and check for collisions
    const tempDocuments = convertItemsToTempDocuments(
      itemsToCheck,
      props.parentId || "",
      teamName,
      props.defaultDocumentType
    );

    const collisions = checkBatchVersionCollisions(
      tempDocuments,
      () => ({}), // empty object = new document
      collectionToUse
    );

    if (collisions.length > 0) {
      const message = renderVersionCollisionMessage(collisions);
      addLocalNotification({
        type: "info",
        content: message,
      });
    }
  }, [
    files,
    fileMetadata,
    props.versioningEnabled,
    props.documentCollection,
    props.stagedFiles,
    props.parentId,
    props.defaultDocumentType,
    props.enableReplaceMode,
    props.visible,
    teamName,
    clearNotifications,
    addLocalNotification,
    uploadProgress.isUploading,
  ]);

  // Duplicate detection: compare file names against existing documents
  useEffect(() => {
    // Only run when enableReplaceMode is true
    if (!props.enableReplaceMode || !props.existingDocuments) {
      return;
    }

    const duplicates = new Set<string>();

    // Compare each file name against existing documents (case-sensitive exact match)
    files.forEach((file) => {
      const isDuplicate = props.existingDocuments!.some((doc) => {
        // For Atlas documents, fileName doesn't include extension, so reconstruct full name
        const fullFileName = doc.fileName + (doc.extentionType || "");
        return fullFileName === file.name;
      });
      if (isDuplicate) {
        duplicates.add(file.name);
      }
    });

    setDuplicateFiles(duplicates);
  }, [files, props.existingDocuments, props.enableReplaceMode]);

  const onDismiss = (e: any) => {
    // Prevent closing modal during upload
    if (uploadProgress.isUploading) {
      // Use global notification system so it appears outside the modal
      if (props.addNotification) {
        props.addNotification({
          type: "info",
          content: "Upload in progress. Please wait or click Cancel to abort.",
          dismissible: true,
          timeout: 3000,
        });
      }
      return;
    }
    if (e.detail.reason == "overlay") return;
    resetModal();
  };

  const onCancelSave = () => {
    if (uploadProgress.isUploading) {
      // Set cancel flag to abort ongoing upload
      cancelTokenRef.current.cancelled = true;
      // The upload function will handle cleanup and show notification
    } else {
      resetModal();
    }
  };

  const onCreate = async (e: any) => {
    // Validate files and metadata based on enabled features
    if (props.enableRenaming || props.enableDocumentTypes) {
      const validation = validateFileMetadata(
        fileMetadata,
        props.requireDocumentTypes
      );

      if (!validation.isValid) {
        setFileError(true);
        return;
      }

      setIsDisabled(true);
      // Reset cancel token for new upload
      cancelTokenRef.current = { cancelled: false };
      // Freeze current notifications before upload starts
      frozenNotificationsRef.current = [...notifications];

      if (props.onUpload) {
        props.onUpload(
          fileMetadata,
          () => {
            resetModal();
          },
          (progress: UploadProgress) => {
            setUploadProgress(progress);
          },
          cancelTokenRef.current
        );
      }
    } else {
      if (files.length === 0) {
        setFileError(true);
        return;
      }

      // OpenSearch collision detection (active confirmation for team files)
      if (
        useOpenSearchCollision &&
        props.searchDocuments &&
        !showOSCollisionWarning &&
        props.versioningEnabled
      ) {
        setIsCheckingCollisions(true);
        try {
          // Convert files to temp documents for collision checking
          const tempDocs: DocumentNew[] = files.map((file) => {
            const extension = getFileExtension(file.name);
            const baseName = getFileNameWithoutExtension(file.name);

            return {
              documentId: "",
              fileName: baseName,
              extensionType: extension,
              documentType: props.defaultDocumentType || "Team",
              parentId: props.parentId || "",
              teamName,
              createdAt: new Date().toISOString(),
              modifiedAt: new Date().toISOString(),
              createdBy: "",
              modifiedBy: "",
              size: `${file.size} bytes`,
              isFolder: false,
              deleted: false,
              securityLevel: "Confidential",
            };
          });

          const collisions = await checkBatchVersionCollisionsOS(
            tempDocs,
            () => ({}), // empty object = new document
            props.searchDocuments
          );

          setIsCheckingCollisions(false);

          if (collisions.length > 0) {
            setOSCollisions(collisions);
            setShowOSCollisionWarning(true);
            return; // Stop here - show collision warning
          }
        } catch (error) {
          console.error("Error checking collisions:", error);
          setIsCheckingCollisions(false);
          // Proceed with upload even if collision check fails
        }
      }

      // Proceed with upload (no collisions OR after confirmation)
      setIsDisabled(true);
      // Reset cancel token for new upload
      cancelTokenRef.current = { cancelled: false };
      // Freeze current notifications before upload starts
      frozenNotificationsRef.current = [...notifications];

      if (props.onCreate) {
        props.onCreate(
          files,
          teamName,
          () => {
            resetModal();
          },
          replaceExisting,
          props.existingDocuments || [],
          (progress: UploadProgress) => {
            setUploadProgress(progress);
          },
          cancelTokenRef.current
        );
      }
    }
  };

  const resetModal = () => {
    props.setVisible(false);
    setFiles([]);
    setFileMetadata([]);
    setFileError(false);
    setIsDisabled(false);
    setReplaceExisting(false);
    setDuplicateFiles(new Set());
    setUploadProgress(createInitialUploadProgress());
    cancelTokenRef.current = { cancelled: false };
    frozenNotificationsRef.current = [];
    // Reset OpenSearch collision state
    setShowOSCollisionWarning(false);
    setOSCollisions([]);
    setOSCollisionAcknowledged(false);
    setIsCheckingCollisions(false);
  };

  const handleFileMetadataChange = (metadata: FileMetadata[]) => {
    setFileMetadata(metadata);
    setFileError(false);
  };

  const getErrorMessage = () => {
    if (!fileError) return undefined;
    if (props.enableRenaming || props.enableDocumentTypes) {
      const validation = validateFileMetadata(
        fileMetadata,
        props.requireDocumentTypes
      );
      return validation.errorMessage;
    } else {
      return "Please select files";
    }
  };

  return (
    <Modal
      visible={props.visible}
      onDismiss={onDismiss}
      header={props.title || "Upload files"}
      data-testid="input-files-modal"
      size={props.enableDocumentTypes ? "large" : "medium"}
    >
      <div className={styles.modalContent}>
        <div className={styles.modalBody}>
          {props.description && (
            <div>
              {props.description}
              <br />
              <br />
            </div>
          )}

          {(uploadProgress.isUploading
            ? frozenNotificationsRef.current
            : notifications
          ).length > 0 && (
            <div style={{ marginBottom: "16px" }}>
              <Flashbar
                items={(uploadProgress.isUploading
                  ? frozenNotificationsRef.current
                  : notifications
                ).map((notification) => ({
                  ...notification,
                }))}
              />
            </div>
          )}

          <InputFiles
            setFiles={setFiles}
            files={files}
            enableRenaming={props.enableRenaming}
            enableDocumentTypes={props.enableDocumentTypes}
            requireDocumentTypes={props.requireDocumentTypes}
            onFileMetadataChange={handleFileMetadataChange}
            fileMetadata={fileMetadata}
            acceptedFileTypes={props.acceptedFileTypes}
            documentTypeOptions={props.documentTypeOptions}
            defaultDocumentType={props.defaultDocumentType}
            gaiaObjectType={props.gaiaObjectType}
            dragAndDropEnabled={props.dragAndDropEnabled}
            isUploading={uploadProgress.isUploading}
          />

          {showOSCollisionWarning && osCollisions.length > 0 && (
            <div style={{ marginTop: "16px" }}>
              <Container
                header={
                  <Header variant="h3">
                    <span style={{ color: "#d13212" }}>
                      <Icon name="status-warning" /> Version Collision Warning
                    </span>
                  </Header>
                }
              >
                <SpaceBetween size="m">
                  {renderVersionCollisionMessageOS(osCollisions)}
                  <Checkbox
                    checked={osCollisionAcknowledged}
                    onChange={({ detail }) =>
                      setOSCollisionAcknowledged(detail.checked)
                    }
                  >
                    I understand these files will become versions of existing
                    files
                  </Checkbox>
                </SpaceBetween>
              </Container>
            </div>
          )}

          {duplicateFiles.size > 0 && (
            <div style={{ marginTop: "16px" }}>
              <Checkbox
                checked={replaceExisting}
                onChange={({ detail }) => setReplaceExisting(detail.checked)}
              >
                Replace existing files with the same name ({duplicateFiles.size}{" "}
                file{duplicateFiles.size > 1 ? "s" : ""})
              </Checkbox>
            </div>
          )}

          {fileError && (
            <div
              style={{ color: "#d13212", marginTop: "8px", fontSize: "14px" }}
            >
              {getErrorMessage()}
            </div>
          )}

          {uploadProgress.isUploading && (
            <div ref={progressBarRef}>
              <Box margin={{ top: "m" }}>
                <ProgressBar
                  value={uploadProgress.currentFileProgress}
                  label={`Uploading ${uploadProgress.currentFileIndex} of ${uploadProgress.totalFiles}: ${uploadProgress.currentFileName}`}
                  status={
                    uploadProgress.cancelRequested ? "error" : "in-progress"
                  }
                />
              </Box>
            </div>
          )}
        </div>
        <div className={styles.modalFooter}>
          <Box>
            <SpaceBetween direction="horizontal" size="xs">
              <Button formAction="none" onClick={onCancelSave} variant="link">
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={
                  isDisabled ||
                  isCheckingCollisions ||
                  (files.length === 0 && fileMetadata.length === 0) ||
                  (showOSCollisionWarning && !osCollisionAcknowledged)
                }
                onClick={onCreate}
              >
                {isDisabled || isCheckingCollisions ? <Spinner /> : null}
                {isCheckingCollisions
                  ? "Checking..."
                  : showOSCollisionWarning
                  ? "Confirm and Upload"
                  : "Upload"}
              </Button>
            </SpaceBetween>
          </Box>
        </div>
      </div>
    </Modal>
  );
};

export default InputFilesModal;
